import { Form, Input, message, Select, Spin } from "antd";
import searchIcon from "@assets/icons/search.svg";
import { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import DraggableOption from "./DraggableOption";
import {
  useDeleteSavedAdvanceSearchMutation,
  useFetchAdvanceSearchFieldsQuery,
  useFetchPrimaryDropdownsQuery,
  useFetchSavedAdvanceSearchFiltersQuery,
  useUpdateAdvanceSearchFieldsMutation,
} from "@store/advanceSearch";
import {
  DeleteOutlined,
  Loading3QuartersOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { PrimaryButton, SecondaryButton } from "@common/Buttons";

export default function SearchArea({
  setSelectedFilters,
  selectedFilters,
  setSelectedPrimaryProducts,
  selectedPrimaryProducts,
  handleSearch,
  loadingData,
  filters,
  setFilters,
  setSecondaryFilters,
  hiddenFilters,
  setHiddenFilters,
  onHandleSaveSearch,
  isAdvanceFilterSaving,
  isSaveSearchFormOpen,
  setIsSaveSearchFormOpen,
  setSelectedSavedSearch,
}) {
  const [search, setSearch] = useState("");
  const [draggedItem, setDraggedItem] = useState(null);
  const [
    shouldCallUpdateFilterSequenceAPI,
    setShouldCallUpdateFilterSequenceAPI,
  ] = useState(false);
  const [form] = Form.useForm();

  const {
    data: saveAdvanceSearchesData,
    isLoading: isSvedAdvanceSearchedLoading,
    isFetching: isSavedAdvanceSearchedFetching,
  } = useFetchSavedAdvanceSearchFiltersQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: primaryFilters,
    isLoading: isPrimaryFiltersLoading,
    isFetching: isPrimaryFiltersFetching,
  } = useFetchAdvanceSearchFieldsQuery({}, { refetchOnMountOrArgChange: true });

  const [updateAdvanceSearchOrdering] = useUpdateAdvanceSearchFieldsMutation();

  const [
    deleteSavedAdvanceSearchFilters,
    { isLoading: isAdvanceFilterDeleting },
  ] = useDeleteSavedAdvanceSearchMutation();

  const {
    data: primaryFiltersDropdowns,
    isLoading: isPrimaryFiltersDropdownsLoading,
    isFetching: isPrimaryFiltersDropdownsFetching,
  } = useFetchPrimaryDropdownsQuery(
    {
      primaryFieldName: selectedFilters.at(-1)?.name,
      search,
    },
    { refetchOnMountOrArgChange: true, skip: !selectedFilters.length }
  );

  // Debounce the search
  const debouncedOnSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
      }, 300),
    []
  );

  const handleDeleteSavedSearch = (id) => {
    const payload = {
      id,
    };
    deleteSavedAdvanceSearchFilters(payload);
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, target, index = null) => {
    e.preventDefault();

    // IMPORTANT: prevent parent drop from firing
    e.stopPropagation();

    if (!draggedItem) return;

    setShouldCallUpdateFilterSequenceAPI(true);
    /** -----------------------
     *  SELECT AREA DROP
     * ------------------------*/
    if (target === "selected") {
      //reseting primary search on selected primary filter change
      setSelectedPrimaryProducts([]);
      // Move old selected to filters
      if (selectedFilters.length) {
        const prevSelected = selectedFilters[0];
        setFilters((prev) => [...prev, { ...prevSelected }]);
      }

      // Remove dragged from filters and set as only selected
      setFilters((prev) => prev.filter((f) => f.id !== draggedItem.id));

      setSelectedFilters([{ ...draggedItem, sequence: 1 }]);

      setDraggedItem(null);
      return;
    }

    /** -----------------------
     *  AVAILABLE DROP (send selected back)
     * ------------------------*/
    if (target === "available" && index === null) {
      // only executed when dropping from selected → available
      setSelectedFilters([]);

      setFilters((prev) => {
        const updated = [...prev, draggedItem];
        return updated.map((f, i) => ({ ...f, sequence: i + 2 }));
      });

      setDraggedItem(null);
      return;
    }

    /** -----------------------
     *  REORDER INSIDE AVAILABLE LIST
     * ------------------------*/
    if (target === "reorder" && index !== null) {
      setFilters((prev) => {
        const updated = [...prev];

        const oldIndex = updated.findIndex((f) => f.id === draggedItem.id);
        const [removed] = updated.splice(oldIndex, 1);
        updated.splice(index, 0, removed);

        // recalc sequence starting from 2
        return updated.map((f, i) => ({ ...f, sequence: i + 2 }));
      });

      // NOTE: selectedFilters is NOT touched (fix for your bug)

      setDraggedItem(null);
      return;
    }
  };

  const handleSaveSearch = () => {
    if (isSaveSearchFormOpen) {
      form
        .validateFields()
        .then((values) => {
          onHandleSaveSearch(values.save_search);
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      setIsSaveSearchFormOpen(true);
    }
  };

  useEffect(() => {
    if (primaryFilters?.data && primaryFilters?.data?.length) {
      const firstSeqOption = primaryFilters.data.find(
        (item) => item.sequence === 1
      );
      setSelectedFilters([firstSeqOption]);
      setFilters(
        primaryFilters.data.filter((item) => item.id !== firstSeqOption.id)
      );
    }
  }, [primaryFilters]);

  //updating sequence on reordering
  useEffect(() => {
    if (filters.length && selectedFilters.length && shouldCallUpdateFilterSequenceAPI) {
      const reSequencedFilters = filters.map((filter, index) => ({
        ...filter,
        sequence: index + 2,
      }));

      const payload = {
        payload: [
          { ...selectedFilters[0], sequence: 1 },
          ...reSequencedFilters,
        ],
      };

      updateAdvanceSearchOrdering(payload).then((res) => {
        if (res?.data?.success) {
          setShouldCallUpdateFilterSequenceAPI(false);
        }
      });
    }
  }, [filters]);

  return (
    <div className="w-[30%] lg:w-[20%] hidden md:flex flex-col gap-2.5 overflow-auto custom-scrollbar border-r border-gray-200 pr-2 pb-12">
      {/* selected options */}
      <div className="space-y-2">
        <div
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, "selected")}
          className="space-y-2 min-h-[2rem]"
        >
          {selectedFilters.length === 0 ? (
            <p className="text-sm text-gray-400">
              Drag filters here to select them
            </p>
          ) : (
            selectedFilters?.map((filter) => (
              <DraggableOption
                key={filter.id}
                item={filter}
                isDragging={draggedItem?.id === filter.id}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                isSelected={true}
                hiddenFilters={hiddenFilters}
                setHiddenFilters={setHiddenFilters}
              />
            ))
          )}
        </div>
      </div>

      {/* search */}
      {!isSaveSearchFormOpen ? (
        <Select
          onChange={(e) => {
            const restrictedAPIs = primaryFiltersDropdowns?.data
              ?.filter((p) => p.isGramProduct)
              .map((p) => p.id);

            const restrictedCount = e.filter((p) =>
              restrictedAPIs.includes(p)
            ).length;

            const hasOtherAPIs = e.some((p) => !restrictedAPIs.includes(p));

            if (restrictedCount > 0 && hasOtherAPIs) {
              message.error(
                "Latanoprost and Bimatoprost cannot be selected with other APIs"
              );
              return;
            }

            setSelectedPrimaryProducts(e);
          }}
          value={selectedPrimaryProducts}
          options={primaryFiltersDropdowns?.data?.map((pr) => {
            return { label: pr.name, value: pr.id };
          })}
          onSearch={(text) => debouncedOnSearch(text)}
          mode="multiple"
          // variant={"borderless"}
          popupMatchSelectWidth={false}
          placeholder={"Search field"}
          showSearch
          filterOption={false}
          suffix={<img src={searchIcon} alt="search icon" />}
          loading={
            isPrimaryFiltersDropdownsLoading ||
            isPrimaryFiltersDropdownsFetching
          }
        />
      ) : (
        <Form form={form} layout="vertical" className="" requiredMark={false}>
          <div className="!grid !grid-cols-1 !gap-2.5">
            {/* First Name */}
            <Form.Item
              label={""}
              className="!mb-0"
              name="save_search"
              rules={[
                { required: true, message: "Please enter save search name" },
              ]}
            >
              <Input
                placeholder="Enter name"
                className="!rounded-lg border-[#D0D5DD] !text-xs !h-8"
              />
            </Form.Item>
          </div>
        </Form>
      )}

      <div
        className={`grid ${
          isSaveSearchFormOpen ? "grid-cols-2" : "grid-cols-3"
        } gap-2`}
      >
        {isSaveSearchFormOpen ? (
          <SecondaryButton
            className={"!flex-1 !w-full"}
            onClick={() => setIsSaveSearchFormOpen(false)}
            disabled={isAdvanceFilterSaving}
          >
            Cancel
          </SecondaryButton>
        ) : (
          <>
            <PrimaryButton
              className={"!flex-1 !w-full"}
              onClick={() => handleSearch(1)}
              loading={loadingData}
            >
              Search
            </PrimaryButton>
            <SecondaryButton
              className={"!flex-1 !w-full"}
              onClick={() => {
                setSecondaryFilters([]);
                setSelectedPrimaryProducts([]);
                // handleSearch(1)
              }}
              loading={loadingData}
            >
              Reset
            </SecondaryButton>
          </>
        )}
        <PrimaryButton
          className={"!flex-1 !w-full"}
          onClick={handleSaveSearch}
          loading={loadingData || isAdvanceFilterSaving}
        >
          Save
        </PrimaryButton>
      </div>

      {/* un selected options */}
      <div className="space-y-2">
        <label className="text-sm font-semibold text-gray-700">Filters</label>
        {isPrimaryFiltersFetching || isPrimaryFiltersLoading ? (
          <div className="flex items-center justify-center">
            <Spin
              indicator={<LoadingOutlined className="text-primary-tint" />}
            />
          </div>
        ) : (
          <div
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, "available")}
            className="space-y-2"
          >
            {filters.length === 0 ? (
              <p className="text-sm text-gray-400">No filters available</p>
            ) : (
              filters.map((filter, index) => (
                <div
                  key={filter.id}
                  onDrop={(e) => handleDrop(e, "reorder", index)}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <DraggableOption
                    item={filter}
                    isDragging={draggedItem?.id === filter.id}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    isSelected={false}
                    hiddenFilters={hiddenFilters}
                    setHiddenFilters={setHiddenFilters}
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* saved searches */}
      <div className="mt-2 space-y-2">
        <label className="text-sm font-semibold text-gray-700">
          Saved Searches
        </label>

        {!saveAdvanceSearchesData?.data?.length ? (
          <p className="text-sm text-gray-400">No saved searches to show</p>
        ) : isSvedAdvanceSearchedLoading || isSavedAdvanceSearchedFetching ? (
          <div className="flex items-center justify-center">
            <Spin
              indicator={<LoadingOutlined className=" !text-primary-tint" />}
            />
          </div>
        ) : (
          <div className="grid gap-2">
            {saveAdvanceSearchesData?.data?.map((item) => {
              return (
                <div
                  key={item.id}
                  className="flex items-center justify-between h-8 px-3 border rounded-md cursor-pointer hover:bg-primary-tint hover:text-white border-primary-tint text-primary-tint"
                  onClick={(e) => {
                    setSelectedSavedSearch(item.payload);
                  }}
                >
                  <label>{item.name || ""}</label>
                  {isAdvanceFilterDeleting ? (
                    <Spin
                      spinning
                      indicator={
                        <Loading3QuartersOutlined
                          spin
                          className="!text-primary-tint"
                        />
                      }
                      size="small"
                    />
                  ) : (
                    <DeleteOutlined
                      className="text-gray-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSavedSearch(item.id);
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
